import React, { useEffect, useState, useRef } from 'react';
import { SummaryBar } from './SummaryBar';
import { TimelineItem } from './TimelineItem';
import { CreateActivityForm } from './CreateActivityForm';
import { mockActivities, mockMetrics, ActivityType, Activity } from '../data/mockData';
import { HistoryIcon, FilterIcon, PhoneIcon, MailIcon, CalendarIcon, StickyNoteIcon, XIcon } from 'lucide-react';
const FILTER_OPTIONS: {
  type: ActivityType;
  label: string;
  icon: React.ReactNode;
  color: string;
}[] = [{
  type: 'call',
  label: 'Calls',
  icon: <PhoneIcon className="w-4 h-4" data-id="element-3" />,
  color: 'text-emerald-600'
}, {
  type: 'email',
  label: 'Emails',
  icon: <MailIcon className="w-4 h-4" data-id="element-4" />,
  color: 'text-blue-600'
}, {
  type: 'event',
  label: 'Events',
  icon: <CalendarIcon className="w-4 h-4" data-id="element-5" />,
  color: 'text-indigo-600'
}, {
  type: 'note',
  label: 'Notes',
  icon: <StickyNoteIcon className="w-4 h-4" data-id="element-6" />,
  color: 'text-gray-600'
}];
export function ActivityTimeline() {
  const [activities, setActivities] = useState<Activity[]>(mockActivities);
  const [activeFilters, setActiveFilters] = useState<Set<ActivityType>>(new Set());
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setShowFilterDropdown(false);
      }
    }
    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilterDropdown]);
  const toggleFilter = (type: ActivityType) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };
  const clearFilters = () => {
    setActiveFilters(new Set());
  };
  const handleAddActivity = (activity: Activity) => {
    setActivities(prev => [activity, ...prev]);
  };
  // If no filters active, show all; otherwise show only selected types
  const filteredActivities = activeFilters.size === 0 ? activities : activities.filter(a => activeFilters.has(a.type));
  const activeCount = activeFilters.size;
  return <div className="w-full max-w-5xl mx-auto" data-id="element-7">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-6" data-id="element-8">
        <div data-id="element-9">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-id="element-10">
            <HistoryIcon className="w-6 h-6 text-blue-600" data-id="element-11" />
            Consolidated Activity
          </h2>
          <p className="text-sm text-gray-500 mt-1" data-id="element-12">
            Comprehensive timeline of all interactions with Sarah Mitchell
          </p>
        </div>

        {/* Filter Dropdown */}
        <div className="relative" ref={filterRef} data-id="element-13">
          <button onClick={() => setShowFilterDropdown(v => !v)} className={`inline-flex items-center px-4 py-2.5 text-sm font-medium rounded-lg shadow-sm transition-colors border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${activeCount > 0 ? 'bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`} data-id="element-14">
            <FilterIcon className={`w-4 h-4 mr-2 ${activeCount > 0 ? 'text-blue-500' : 'text-gray-500'}`} data-id="element-15" />
            Filter
            {activeCount > 0 && <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-blue-600 rounded-full" data-id="element-16">
                {activeCount}
              </span>}
          </button>

          {showFilterDropdown && <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 z-20 py-2 origin-top-right transition-all" data-id="element-17">
              <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between" data-id="element-18">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-19">
                  Activity Type
                </span>
                {activeCount > 0 && <button onClick={clearFilters} className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded px-1" data-id="element-20">
                    <XIcon className="w-3 h-3" data-id="element-21" />
                    Clear
                  </button>}
              </div>
              {FILTER_OPTIONS.map(opt => {
            const isActive = activeFilters.has(opt.type);
            return <button key={opt.type} onClick={() => toggleFilter(opt.type)} className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors focus-visible:outline-none focus-visible:bg-gray-50 ${isActive ? 'bg-blue-50/50 text-blue-800 font-medium' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-22">
                    <span className={`flex items-center justify-center w-8 h-8 rounded-lg ${isActive ? 'bg-blue-100' : 'bg-gray-100'} ${isActive ? 'text-blue-600' : opt.color}`} data-id="element-23">
                      {opt.icon}
                    </span>
                    <span data-id="element-24">{opt.label}</span>
                    {isActive && <span className="ml-auto w-2 h-2 rounded-full bg-blue-600" data-id="element-25" />}
                  </button>;
          })}
            </div>}
        </div>
      </div>

      {/* Summary Metrics Bar */}
      <SummaryBar metrics={mockMetrics} data-id="element-26" />

      {/* Timeline Container */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8" data-id="element-27">
        <div className="flex items-center justify-between mb-8 border-b border-gray-100 pb-4" data-id="element-28">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2" data-id="element-29">
            Interaction History
            <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full" data-id="element-30">
              {activities.length}
            </span>
          </h3>
          {activeCount > 0 && <span className="text-sm text-gray-500" data-id="element-31">
              Showing {filteredActivities.length} of {activities.length}{' '}
              activities
            </span>}
        </div>

        <div className="relative" data-id="element-32">
          {/* Create Activity Form */}
          <CreateActivityForm onAddActivity={handleAddActivity} data-id="element-33" />

          {/* Chronological List */}
          <div className="space-y-0 relative z-0" data-id="element-34">
            {filteredActivities.length > 0 ? filteredActivities.map((activity, index) => <TimelineItem key={activity.id} activity={activity} isLast={index === filteredActivities.length - 1} data-id="element-35" />) : <div className="text-center py-12" data-id="element-36">
                <FilterIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" data-id="element-37" />
                <p className="text-gray-500 font-medium" data-id="element-38">
                  No activities match your filters
                </p>
                <button onClick={clearFilters} className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded px-2 py-1" data-id="element-39">
                  Clear all filters
                </button>
              </div>}
          </div>
        </div>

        {/* Load More (Mock) */}
        {filteredActivities.length > 0 && <div className="mt-8 pt-4 text-center border-t border-gray-50" data-id="element-40">
            <button className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded px-3 py-1.5" data-id="element-41">
              Load older activities...
            </button>
          </div>}
      </div>
    </div>;
}